import { Injectable } from "@angular/core";
import { Observable, asyncScheduler, throwError } from "rxjs";
import { catchError, map, observeOn, shareReplay, tap } from "rxjs/operators";

import { Form } from "../models/form";
import { AppHttpClient } from "./http.client";

@Injectable({
  providedIn: "root",
})
export class FormsService {
  private formsList$?: Observable<Form[]>;

  constructor(private http: AppHttpClient) {}

  get(formId: string): Observable<Form> {
    return this.http.get<Form>(`forms/${formId}`);
  }

  delete(formId: string): Observable<boolean> {
    return this.http
      .delete<boolean>(`forms?id=${formId}`)
      .pipe(tap(() => this.invalidateFormsListCache()));
  }

  unpublish(formId: string): Observable<boolean> {
    return this.http
      .put<boolean>(`forms/unpublish?formId=${formId}`, {})
      .pipe(tap(() => this.invalidateFormsListCache()));
  }

  getByCode(formCode: string, status: string = "Published"): Observable<Form> {
    return this.http.get<Form>(`forms/by-code/${formCode}?status=${status}`);
  }

  list(forceRefresh = false): Observable<Form[]> {
    if (forceRefresh) {
      this.invalidateFormsListCache();
    }

    if (!this.formsList$) {
      this.formsList$ = this.http.get<Form[]>(`forms/list`).pipe(
        catchError((error) => {
          this.invalidateFormsListCache();
          return throwError(() => error);
        }),
        shareReplay(1)
      );
    }

    return this.formsList$.pipe(
      map((forms) => [...forms]),
      observeOn(asyncScheduler)
    );
  }

  refreshList(): Observable<Form[]> {
    return this.list(true);
  }

  getWorkflow(formId: string): Observable<Form> {
    return this.http.get<Form>(`forms/${formId}`);
  }

  create(form: Form): Observable<Form> {
    return this.http
      .post<Form>(`forms`, form)
      .pipe(tap(() => this.invalidateFormsListCache()));
  }

  createWithColumns(data: any): Observable<Form> {
    return this.http
      .post<Form>(`forms/insert-with-columns`, data)
      .pipe(tap(() => this.invalidateFormsListCache()));
  }

  updateCustom(form: Form): Observable<Form> {
    return this.http
      .put<Form>(`forms/update-custom`, form)
      .pipe(tap(() => this.invalidateFormsListCache()));
  }

  updateWithColumns(data: any): Observable<Form> {
    return this.http
      .put<Form>(`forms/update-with-columns`, data)
      .pipe(tap(() => this.invalidateFormsListCache()));
  }

  publish(formCode: string, formStatus: string): Observable<Form> {
    return this.http
      .post<Form>(`forms/publish/${formCode}?status=${formStatus}`, {})
      .pipe(tap(() => this.invalidateFormsListCache()));
  }

  clone(formId: string): Observable<Form> {
    return this.http
      .post<Form>(`forms/clone?formId=${formId}`, {})
      .pipe(tap(() => this.invalidateFormsListCache()));
  }

  restore(id: string): Observable<Form> {
    return this.http
      .post<Form>(`forms/restore?id=${id}`, {})
      .pipe(tap(() => this.invalidateFormsListCache()));
  }

  private invalidateFormsListCache(): void {
    this.formsList$ = undefined;
  }
}
